import type { SessionStepApiBindingDto, SessionStepDto } from '../../schemas/sessionSchemas';

export function isAadhaarStep(step: SessionStepDto) {
  return /aadhaar|adhar/i.test(step.stepType) || /aadhaar|adhar/i.test(step.displayName);
}

// Which live screen an Aadhaar API binding maps to. Keyed off the catalogue
// operationId (Post_Aadhaar_Digilocker_Pinless / _Pin / _SecureQr / _Ovse /
// _Ocr) with a path fallback. DigiLocker (pin/pinless) and OVSE are async
// webhook flows; Secure QR and OCR are synchronous (scan/upload → scored on the
// response).
export type AadhaarScreenKind =
  | 'digilocker-pinless'
  | 'digilocker-pin'
  | 'secure-qr'
  | 'ovse'
  | 'ocr';

/**
 * What to CALL each Aadhaar method to the applicant.
 *
 * Every one of these changes the screen materially — a QR scan, a photo, a DigiLocker
 * hand-off, an OTP — so naming the next one is the difference between "the page changed for
 * no reason" and "I know what I'm being asked for".
 *
 * Both DigiLocker variants read the same: whether a PIN is needed is decided inside
 * DigiLocker's own flow, and it is not a distinction the applicant chooses.
 */
export const AADHAAR_METHOD_LABEL: Record<AadhaarScreenKind, string> = {
  'digilocker-pinless': 'DigiLocker',
  'digilocker-pin': 'DigiLocker',
  'secure-qr': 'the QR code on your Aadhaar',
  ovse: 'an Aadhaar OTP',
  ocr: 'a photo of your Aadhaar',
};

export function aadhaarScreenKind(
  binding: Pick<SessionStepApiBindingDto, 'operationId' | 'path'>,
): AadhaarScreenKind {
  const key = `${binding.operationId} ${binding.path}`.toLowerCase();
  if (key.includes('secure') || key.includes('secure-qr')) return 'secure-qr';
  if (key.includes('ocr') || key.includes('kycocr')) return 'ocr';
  if (key.includes('ovse')) return 'ovse';
  if (key.includes('digilocker') || key.includes('digl')) {
    return key.includes('pinless') ? 'digilocker-pinless' : 'digilocker-pin';
  }
  // Default: PIN-less DigiLocker (the designer's primary Aadhaar method).
  return 'digilocker-pinless';
}

// True for the async flows (DigiLocker / OVSE) that complete via our webhook.
// Secure QR and OCR are synchronous (scored on the init/read response).
export function isAsyncKind(kind: AadhaarScreenKind) {
  return kind !== 'ocr' && kind !== 'secure-qr';
}

// Recursively find the first string value under a key that looks like the
// provider's redirect/QR URL (DigiLocker init → data.requestUrl; OVSE → a
// request/qr url). Returns null when none is present (e.g. a technical error).
const URL_KEY = /(request.?url|redirect.?url|qr.?url|verification.?url|^url$)/i;

export function findRequestUrl(resultJson: string | null): string | null {
  if (!resultJson) return null;
  try {
    return searchUrl(JSON.parse(resultJson));
  } catch {
    return null;
  }
}

function searchUrl(node: unknown): string | null {
  if (node && typeof node === 'object') {
    if (!Array.isArray(node)) {
      for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
        if (URL_KEY.test(k) && typeof v === 'string' && /^https?:\/\//i.test(v)) {
          return v;
        }
      }
    }
    for (const v of Object.values(node as Record<string, unknown>)) {
      const found = searchUrl(v);
      if (found) return found;
    }
  }
  return null;
}

// Best-effort error message from a provider error envelope (mirrors PAN).
export function extractAadhaarError(resultJson: string | null): string | null {
  if (!resultJson) return null;
  try {
    const parsed = JSON.parse(resultJson) as Record<string, unknown>;
    for (const key of ['message', 'error', 'detail']) {
      const val = parsed[key];
      if (typeof val === 'string' && val.trim()) return val.trim();
    }
    return null;
  } catch {
    return null;
  }
}

// The human reason the backend attached to a failed/review result
// (`verification_reason`) — shown on the applicant's failure screen.
export function extractAadhaarReason(resultJson: string | null): string | null {
  if (!resultJson) return null;
  try {
    const val = searchReason(JSON.parse(resultJson));
    return val && val.trim() ? val.trim() : null;
  } catch {
    return null;
  }
}

function searchReason(node: unknown): string | null {
  if (node && typeof node === 'object') {
    if (!Array.isArray(node)) {
      const obj = node as Record<string, unknown>;
      if (typeof obj.verification_reason === 'string') return obj.verification_reason;
    }
    for (const v of Object.values(node as Record<string, unknown>)) {
      const found = searchReason(v);
      if (found) return found;
    }
  }
  return null;
}
